import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../../../store";
import { FiPaperclip, FiDownload, FiLoader, FiMaximize2, FiShare2, FiCode, FiMap, FiChevronDown, FiChevronUp, FiThumbsUp, FiThumbsDown } from "react-icons/fi";
// Filled variants for the active rating state — the olive accent alone is too faint on the pale AI bubble.
import { FaThumbsUp, FaThumbsDown } from "react-icons/fa";
import { ChatMessage, updateAiMessageFromSubscription } from "../../../store/aiChatSlice";
import { MessageRating } from "../../../services/conversationService";
import { MENTION_TERMINATOR, MENTION_REGEX, buildAssetLabel } from "../hooks/useAtMention";
import { renderMentionChipInner } from "./AtMentionPicker";
import {
  MCPService,
  AttachedFile,
  normalizeAiMessageText,
} from "../../../services/mcpService";
import MessageWithChart from "../../../components/DataVisualization/MessageWithChart";
import MiniMap from "../../../components/DataVisualization/MiniMap";
import { convertKMLToGeoJSON } from "../../../utils/geojsonUtils";
import ProfilePictureRounded from "../../ProfilePictureRounded";
import { ReactComponent as ErodeLogo } from "../../../assets/images/erode-logo-black.svg";
import { ReactComponent as HomeSmile } from "../../../assets/icons/home-smile.svg";
import ChatBubbleModal from "./ChatBubbleModal";
import { setModalState, modalViewsConst } from "../../../store/modalSlice";
import { parseUpdateBlocks, groupStepsByReasoning } from "../../../utils/chatUpdates";
import { stripAssetContext } from "../../../utils/assetContext";
import { usePostHogCapture } from "../../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../../utils/posthogEvents";
import {
  CHAT_FEEDBACK_COMMENT_MAX,
  persistMessageRating,
  submitChatThumbsDown,
} from "../../../utils/chatFeedback";
import { SkeletonGroup, SkeletonText } from "../../Skeleton";
// This component renders the list of chat messages in the AI Chat panel.

// Renders message text with @mention chips; chips are clickable when the asset is found in the store. In readOnly (shared/public) contexts the chips render as inert, non-clickable spans.
const MessageWithMentions: React.FC<{ text: string; readOnly?: boolean }> = ({ text, readOnly = false }) => {
  const navigate = useNavigate();
  const dataGroups = useSelector((state: RootState) => state.assets.dataGroups as Record<string, any[]>);
  const assetGroups = useSelector(
    (state: RootState) =>
      state.assetGroups.groups as Record<string, { id: string; name: string; profilePictureUrl?: string }>
  );

  // Label → mentioned entity. Group labels are inserted first so an asset with the exact same label overwrites (assets win collisions — preserves how all existing transcripts rendered before group mentions existed).
  const entityByLabel = React.useMemo(() => {
    const map = new Map<
      string,
      { kind: "asset" | "group"; assetId?: string; groupId: string; entity: any; groupName?: string }
    >();
    Object.values(assetGroups).forEach((group) => {
      map.set(group.name, {
        kind: "group",
        groupId: group.id,
        entity: { name: group.name, imageUrl: group.profilePictureUrl },
      });
    });
    Object.entries(dataGroups).forEach(([groupId, assets]) => {
      const groupName: string = assetGroups[groupId]?.name ?? "";
      (assets as any[]).forEach((asset) => {
        const label = buildAssetLabel(groupName, asset.name);
        map.set(label, { kind: "asset", assetId: asset.id, groupId, entity: asset, groupName });
      });
    });
    return map;
  }, [dataGroups, assetGroups]);

  const regex = new RegExp(MENTION_REGEX.source, MENTION_REGEX.flags);
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const raw = match[0].replace(MENTION_TERMINATOR, ""); // strip zero-width space terminator
    const label = raw.slice(1); // strip leading @
    const found = entityByLabel.get(label);

    // Same pill shape as the input's inline MentionPill: rounded-full, entity name only (no "Group /" prefix). my-0.5 gives wrapped lines a visible gap so consecutive chip rows don't butt up against (or visually overlap) each other.
    const chipClassName =
      "inline-flex items-center gap-1 bg-eai-cta-primary/30 text-eai-cta-primary font-medium rounded-full px-1.5 py-px my-0.5 text-sm align-baseline";

    if (found && !readOnly) {
      const target =
        found.kind === "group"
          ? `/home/group/${found.groupId}`
          : `/home/group/${found.groupId}/asset/${found.assetId}`;
      parts.push(
        <button
          key={match.index}
          onClick={() => navigate(target)}
          className={`${chipClassName} hover:bg-eai-cta-primary/40 transition-colors cursor-pointer`}
        >
          {renderMentionChipInner(found.entity, undefined, {
            size: 16,
            groupNameClass: "text-eai-cta-primary/60",
            fallbackLabel: label,
          })}
        </button>
      );
    } else if (found && readOnly) {
      // Inert chip: same look as a found chip but not clickable
      parts.push(
        <span key={match.index} className={chipClassName}>
          {renderMentionChipInner(found.entity, undefined, {
            size: 16,
            groupNameClass: "text-eai-cta-primary/60",
            fallbackLabel: label,
          })}
        </span>
      );
    } else {
      // Unresolved mention: the live app hits this when a mentioned asset was since deleted; shared/embed (logged-out) pages hit it for every mention, since the asset store never loads there. Either way, show only the entity's own name — not the full "Group / Asset" token — matching a resolved chip's display. TODO: shared/embed pages still show a generic icon instead of the asset's thumbnail. Fixing that needs mentioned-entity metadata (name/image) included in the shared-conversation payload from the backend.
      const displayLabel = label.includes(" / ")
        ? label.slice(label.lastIndexOf(" / ") + 3)
        : label;
      parts.push(
        <span key={match.index} className={chipClassName}>
          <HomeSmile className="h-3 w-3 shrink-0" />
          <span>{displayLabel}</span>
        </span>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts.length > 0 ? parts : text}</>;
};

// Format timestamp for display (e.g., "Mar 29, 1:56pm")
const formatMessageTime = (isoString?: string): string => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  const displayHours = hours % 12 || 12;
  return `${month} ${day}, ${displayHours}:${minutes}${ampm}`;
};

// Props for AiChatMessages component
interface AiChatMessagesProps {
  messages: ChatMessage[];
  isLoading: boolean;
  isFetchingConversation: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  conversationId?: string;
  // When true, render in a read-only (shared/public) context: @mention chips are inert, attachment chips and per-message share are hidden, and asset-proposal action UI is hidden inside MessageWithChart.
  readOnly?: boolean;
  scheduleRunnerId?: string;
  shareReturnTo?: { modalView: string; properties: Record<string, unknown> };
  // Re-send the given prompt text. Used by the Retry button on a failed (status "error") AI message.
  onRetry?: (promptText: string) => void;
}

// FileChip component for rendering clickable file attachments
const FileChip: React.FC<{ file: AttachedFile }> = ({ file }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const blob = await MCPService.downloadFile(file.uri);
      MCPService.downloadBlobAsFile(blob, file.filename);
    } catch (err) {
      console.error('Failed to download file:', err);
    } finally {
      setIsDownloading(false);
    }
  }, [file.uri, file.filename]);

  return (
    <button
      onClick={handleDownload}
      disabled={isDownloading}
      className="font-inter flex items-center gap-1.5 rounded-full border border-eai-border bg-eai-surface-control px-2.5 py-1 text-eai-body-xs text-eai-ink-soft transition-colors hover:border-eai-border-hover hover:bg-eai-surface-sunken active:bg-eai-surface-hover disabled:pointer-events-none disabled:opacity-40"
    >
      <FiPaperclip className="h-3 w-3 text-eai-ink-subtle" />
      <span className="max-w-[120px] truncate">{file.filename}</span>
      {isDownloading ? (
        <FiLoader className="h-3 w-3 animate-spin text-eai-ink-subtle" />
      ) : (
        <FiDownload className="h-3 w-3 text-eai-ink-subtle" />
      )}
    </button>
  );
};

// KML attachment chip that fetches and previews geometry on a map, expanded by default; clicking the chip toggles the preview closed/open. In readOnly mode the URI is a public presigned S3 URL; otherwise it goes via the authenticated MCP proxy using MCPService.downloadFile.
const KmlPreviewChip: React.FC<{ file: AttachedFile; readOnly?: boolean; deferLoad?: boolean }> = ({ file, readOnly = false, deferLoad = false }) => {
  // Geometry the user attaches should be visible right away rather than requiring a click, so the map preview starts open and loads its data on mount. The click-to-collapse interaction below is still supported.
  const [showPreview, setShowPreview] = useState(true);
  const [geoJson, setGeoJson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchAndParse = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      let kmlText: string;
      if (readOnly) {
        // Presigned S3 URL — fetch directly, no auth required.
        const resp = await fetch(file.uri);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        kmlText = await resp.text();
      } else {
        const blob = await MCPService.downloadFile(file.uri);
        kmlText = await blob.text();
      }
      const parsed = convertKMLToGeoJSON(kmlText);
      setGeoJson(parsed);
      setError(false);
    } catch (err) {
      console.error("[KmlPreviewChip] Failed to load KML:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [file.uri, readOnly]);

  useEffect(() => {
    if (deferLoad) return;
    fetchAndParse();
    // Only run once on mount — fetchAndParse identity changes if file.uri/readOnly change, but a chip is always re-keyed by file.uri so that can't happen in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = useCallback(async () => {
    if (showPreview) {
      setShowPreview(false);
      return;
    }
    // A fetch is already in flight (e.g. rapid double-click on a slow network): ignore this click. The original click opens the preview once it resolves, so we avoid flipping the preview open to an empty state.
    if (loading) return;
    if (!geoJson) {
      await fetchAndParse();
    }
    setShowPreview(true);
  }, [showPreview, geoJson, loading, fetchAndParse]);

  return (
    <div className="w-full">
      <button
        onClick={handleClick}
        disabled={loading}
        className="font-inter flex items-center gap-1.5 rounded-full border border-eai-border bg-eai-surface-control px-2.5 py-1 text-eai-body-xs text-eai-ink-soft transition-colors hover:border-eai-border-hover hover:bg-eai-surface-sunken active:bg-eai-surface-hover disabled:pointer-events-none disabled:opacity-40"
      >
        {loading ? (
          <FiLoader className="h-3 w-3 animate-spin text-eai-ink-subtle" />
        ) : (
          <FiMap className="h-3 w-3 text-eai-ink-subtle" />
        )}
        <span className="max-w-[120px] truncate">{file.filename}</span>
        {showPreview ? (
          <FiChevronUp className="h-3 w-3 text-eai-ink-subtle" />
        ) : (
          <FiChevronDown className="h-3 w-3 text-eai-ink-subtle" />
        )}
      </button>
      {showPreview && geoJson && (
        <div className="mt-2 w-full overflow-hidden rounded-eai-card border border-eai-border">
          <MiniMap geojson={geoJson} fixedHeight={360} fitParent />
        </div>
      )}
      {showPreview && error && (
        <p className="font-inter mt-1 text-xs text-eai-danger">Failed to load map preview.</p>
      )}
      {/* Expanded but nothing to show yet — covers the deferred wait and the in-flight load so the preview never sits silently empty */}
      {showPreview && !geoJson && !error && (
        <p className="font-inter mt-1 text-xs text-eai-ink-faint">
          {deferLoad ? "Preview loads once the message is sent." : "Loading map preview…"}
        </p>
      )}
    </div>
  );
};

// AiChatMessages component definition
const AiChatMessages: React.FC<AiChatMessagesProps> = ({
  messages,
  isLoading,
  isFetchingConversation,
  messagesEndRef,
  conversationId,
  readOnly = false,
  scheduleRunnerId: scheduleRunnerIdProp,
  shareReturnTo,
  onRetry,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // Share a single message: open the share modal (link-only mode), which creates the share on mount.
  const handleShareMessage = useCallback(
    (messageId: string) => {
      if (!conversationId) return;
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.shareEmbed,
          properties: {
            __modalSize: "sm",
            conversationId,
            messageId,
            ...(shareReturnTo && { __returnTo: shareReturnTo }),
          },
        })
      );
    },
    [conversationId, dispatch, shareReturnTo]
  );

  // Get user information from Redux store
  const user = useSelector((state: any) => state.user);
  const { capture } = usePostHogCapture();

  // Thumbs up/down on AI responses. The rating itself is persisted on the message straight away
  // (optimistic, reverted on failure). A thumbs-down also opens an optional comment box; the
  // ErrorLog for admins is filed when the user sends or skips that comment.
  const [feedbackDraft, setFeedbackDraft] = useState<{
    messageId: string;
    comment: string;
    submitting: boolean;
    error: string | null;
  } | null>(null);
  // Message ids whose thumbs-down has already been filed this session (prevents duplicate logs
  // if the user re-opens the box by clicking thumbs-down again).
  const filedFeedbackRef = useRef<Set<string>>(new Set());
  const [feedbackSentFor, setFeedbackSentFor] = useState<string | null>(null);
  const feedbackSentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (feedbackSentTimerRef.current) clearTimeout(feedbackSentTimerRef.current);
  }, []);

  const handleRate = useCallback(
    async (msg: ChatMessage, rating: MessageRating) => {
      if (!conversationId) return;
      if (msg.rating === rating) {
        // Already rated this way. Re-clicking thumbs-down re-opens the comment box (e.g. after a
        // reload) unless the feedback has already been sent this session.
        if (rating === "DOWN" && !filedFeedbackRef.current.has(msg.id)) {
          setFeedbackDraft({ messageId: msg.id, comment: "", submitting: false, error: null });
        }
        return;
      }
      const previous = msg.rating;
      dispatch(updateAiMessageFromSubscription({ conversationId, messageId: msg.id, updates: { rating } }));
      if (rating === "DOWN") {
        setFeedbackDraft({ messageId: msg.id, comment: "", submitting: false, error: null });
      } else {
        setFeedbackDraft((d) => (d?.messageId === msg.id ? null : d));
      }
      try {
        await persistMessageRating(msg.id, rating);
        // Only count ratings that actually saved
        capture(
          rating === "UP" ? POSTHOG_EVENTS.CHAT_RESPONSE_RATED_UP : POSTHOG_EVENTS.CHAT_RESPONSE_RATED_DOWN,
          { conversation_id: conversationId, message_id: msg.id }
        );
      } catch (err) {
        console.error("Failed to save message rating:", err);
        dispatch(
          updateAiMessageFromSubscription({ conversationId, messageId: msg.id, updates: { rating: previous } })
        );
        setFeedbackDraft((d) => (d?.messageId === msg.id ? null : d));
      }
    },
    [conversationId, dispatch, capture]
  );

  // Send (or skip) the thumbs-down comment: files the ErrorLog for the admin queue.
  const handleSubmitFeedback = useCallback(
    async (msg: ChatMessage, prompt: string | undefined, comment: string) => {
      if (!conversationId) return;
      setFeedbackDraft((d) => (d ? { ...d, submitting: true, error: null } : d));
      try {
        await submitChatThumbsDown({
          conversationId,
          messageId: msg.id,
          prompt,
          answer: msg.text,
          comment,
          userEmail: user?.permissionDetails?.email ?? null,
        });
        filedFeedbackRef.current.add(msg.id);
        const trimmed = comment.trim();
        if (trimmed) {
          dispatch(
            updateAiMessageFromSubscription({
              conversationId,
              messageId: msg.id,
              updates: { ratingComment: trimmed },
            })
          );
        }
        setFeedbackDraft(null);
        setFeedbackSentFor(msg.id);
        if (feedbackSentTimerRef.current) clearTimeout(feedbackSentTimerRef.current);
        feedbackSentTimerRef.current = setTimeout(() => setFeedbackSentFor(null), 4000);
      } catch (err) {
        console.error("Failed to submit chat feedback:", err);
        setFeedbackDraft((d) =>
          d ? { ...d, submitting: false, error: "Couldn't send your feedback. Please try again." } : d
        );
      }
    },
    [conversationId, dispatch, user?.permissionDetails?.email]
  );
  const rawUserName =
    user?.permissionDetails?.name ||
    user?.permissionDetails?.email ||
    "You";
  const userName = rawUserName
    ? rawUserName.charAt(0).toUpperCase() + rawUserName.slice(1) // Capitalize first letter
    : "You";

  // Show a delayed message if the AI is taking longer than 30 seconds
  const [showDelayMessage, setShowDelayMessage] = useState(false);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A persisted "pending" placeholder drives the same "Thinking..." UI as the transient isLoading flag (and survives navigation/reload).
  const hasPendingMessage = messages.some(
    (m) => !m.isUser && m.status === "pending"
  );
  const showThinking = isLoading || hasPendingMessage;

  // Track which message is expanded to full screen via the modal
  const [expanded, setExpanded] = useState<{
    message: ChatMessage;
    triggerText?: string;
  } | null>(null);

  // Live share/embed is only offered for scheduled-agent runs — resolve this conversation's runnerId (guarded to the currently displayed conversation so a stale Redux value can't leak through).
  const currentConversation = useSelector((state: RootState) => state.aiChat.currentConversation);
  const scheduleRunnerId =
    scheduleRunnerIdProp ??
    (currentConversation && currentConversation.id === conversationId
      ? currentConversation.agentScheduleRunnerId || ""
      : "");

  // Open the live share/embed modal through the global modal system (ModalBase flow).
  const openEmbedModal = useCallback(() => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.shareEmbed,
        properties: {
          __modalSize: "sm",
          runnerId: scheduleRunnerId,
          ...(shareReturnTo && { __returnTo: shareReturnTo }),
        },
      })
    );
  }, [dispatch, scheduleRunnerId, shareReturnTo]);

  // Stable reference so ChatBubbleModal's keydown/scroll-lock effect doesn't re-subscribe on every render of this list.
  const handleCloseExpanded = useCallback(() => setExpanded(null), []);

  // Explicit user expand/collapse overrides for a message's <update> steps, keyed by id (true = collapsed). When a message has no override, the steps default to expanded while processing and collapsed once the final answer arrives — see `stepsCollapsed` in the render below.
  const [stepOverrides, setStepOverrides] = useState<Record<string, boolean>>({});
  const toggleSteps = useCallback((id: string, currentlyCollapsed: boolean) => {
    setStepOverrides((prev) => ({ ...prev, [id]: !currentlyCollapsed }));
  }, []);

  // Which steps have their nested reasoning expanded, keyed by `${msgId}-${stepIndex}` (true = open). Defaults to closed: click a step to reveal what was thought.
  const [reasoningOpen, setReasoningOpen] = useState<Record<string, boolean>>({});
  const toggleReasoning = useCallback((key: string) => {
    setReasoningOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  useEffect(() => {
    if (showThinking) {
      delayTimerRef.current = setTimeout(() => {
        setShowDelayMessage(true);
      }, 30000);
    } else {
      setShowDelayMessage(false);
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
    }
    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
    };
  }, [showThinking]);

  // Render the list of chat messages along with loading indicators
  return (
    <>
      {/* Render each message */}
      {(() => {
        let lastUserMessage: ChatMessage | null = null;
        return messages.map((msg) => {
          if (msg.isUser) {
            lastUserMessage = msg;
          }
          const triggerText = lastUserMessage?.text;
          const isPendingMsg = !msg.isUser && msg.status === "pending";
          const isErrorMsg = !msg.isUser && msg.status === "error";
          // Split streamed <update> progress lines from the answer body (AI, non-error).
          const parsed =
            !msg.isUser && !isErrorMsg ? parseUpdateBlocks(msg.text) : null;
          const updateLines = parsed?.updates ?? [];
          // Updates + admin debug reasoning interleaved in arrival order, so each reasoning line shows where it happened relative to the tool steps.
          const steps = parsed?.steps ?? [];
          // Each step owns the reasoning that preceded it, shown nested when expanded.
          const stepGroups = groupStepsByReasoning(steps);
          // The steps section is driven by <update> tool steps; reasoning-only (<thinking>) messages produce no step groups, so gate the section on actual update steps to avoid a visible "0 Processing Steps" divider.
          const hasSteps = updateLines.length > 0;
          // Strip <update>/<thinking> blocks whenever any were present. Fall back to the empty-response message if the answer body is blank (e.g. the AI emitted only progress updates and no final text) so the bubble never renders empty.
          const responseText = steps.length
            ? normalizeAiMessageText(parsed!.response, false)
            : msg.text;
          // Summary shown beside the collapsed steps divider: total tokens + time.
          const stepTotalTokens = (msg.inputTokens ?? 0) + (msg.outputTokens ?? 0);
          const stepMetaParts: string[] = [];
          if (stepTotalTokens > 0) {
            stepMetaParts.push(`${stepTotalTokens.toLocaleString()} tokens`);
          }
          if (msg.executionTimeMs) {
            stepMetaParts.push(`${(msg.executionTimeMs / 1000).toFixed(1)}s`);
          }
          const stepMeta = stepMetaParts.length
            ? ` (${stepMetaParts.join(" · ")})`
            : "";
          // Default: expanded while processing, collapsed once complete. A manual click (stored in stepOverrides) wins over the default.
          const stepsCollapsed =
            msg.id in stepOverrides
              ? stepOverrides[msg.id]
              : msg.status === "complete";
          return (
            <div
              key={msg.id}
              className={hasSteps && !stepsCollapsed ? "mb-3" : "mb-1"}
            >
              {/* Streamed progress steps — rendered ABOVE the response bubble. The outer rule collapses the whole list; each step that has admin debug reasoning is itself clickable to reveal what was thought before it. */}
              {hasSteps && (
                <div
                  className={`max-w-[95%] sm:max-w-[75%] ${
                    stepsCollapsed ? "mb-2" : "mb-4"
                  }`}
                >
                  {/* Horizontal divider — always shown; click to toggle the steps below. */}
                  <div
                    onClick={() => toggleSteps(msg.id, stepsCollapsed)}
                    role="button"
                    aria-expanded={!stepsCollapsed}
                    className="font-inter flex cursor-pointer items-center gap-2 text-sm text-eai-ink-subtle transition-colors hover:text-eai-ink"
                  >
                    <span className="h-0.5 w-6 bg-eai-surface-active" />
                    <span className="whitespace-nowrap">
                      {updateLines.length} Processing Step
                      {updateLines.length === 1 ? "" : "s"}
                      {stepMeta}
                    </span>
                    <span className="h-0.5 flex-1 bg-eai-surface-active" />
                  </div>
                  {/* Steps listed below the divider when expanded. A step with nested reasoning shows a ▸/▾ caret; click it to reveal the thinking. */}
                  {!stepsCollapsed && (
                    <div className="mt-1 px-2 sm:px-3">
                      {stepGroups.map((group, i) => {
                        const reasoningKey = `${msg.id}-${i}`;
                        const hasReasoning = group.thinking.length > 0;
                        const isOpen = !!reasoningOpen[reasoningKey];
                        return (
                          <div key={i}>
                            <div
                              onClick={hasReasoning ? () => toggleReasoning(reasoningKey) : undefined}
                              role={hasReasoning ? "button" : undefined}
                              aria-expanded={hasReasoning ? isOpen : undefined}
                              className={`font-inter flex items-start gap-1 text-sm leading-5 text-eai-ink-subtle ${
                                hasReasoning ? "cursor-pointer hover:text-eai-ink" : ""
                              }`}
                            >
                              {hasReasoning && (
                                <span className="select-none text-eai-ink-faint">
                                  {isOpen ? "▾" : "▸"}
                                </span>
                              )}
                              <span>{group.label}</span>
                            </div>
                            {hasReasoning && isOpen && (
                              <div className="mb-1 ml-4 border-l-2 border-eai-border pl-2">
                                {group.thinking.map((line, j) => (
                                  <div
                                    key={j}
                                    className="mb-1 whitespace-pre-wrap text-eai-body-sm italic text-eai-ink-subtle"
                                  >
                                    {line}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div
                className={`flex items-start gap-1.5 sm:gap-3 ${
                  msg.isUser ? "justify-end" : ""
                }`}
              >
                <div
                  data-testid="chat-message"
                  data-role={msg.isUser ? "user" : "assistant"}
                  className={`font-inter group inline-block min-w-[140px] max-w-[95%] sm:max-w-[75%] whitespace-pre-wrap break-words rounded-eai-card p-2 sm:p-3 text-left text-eai-body-sm shadow-eai-subtle ${
                    msg.isUser
                      ? "border border-eai-border bg-eai-surface-card text-eai-ink-soft"
                      : isErrorMsg
                      ? "border border-eai-danger-border bg-eai-danger-surface text-eai-danger-text"
                      : "border border-eai-border bg-eai-checklist-lt text-eai-ink-soft"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      {msg.isUser ? (
                        <>
                          <ProfilePictureRounded
                            diameter={20}
                            pictureURL={user?.permissionDetails?.profilePictureUrl}
                            className="border-white sm:!w-6 sm:!h-6"
                            onClick={() => {}}
                          />
                          <div className="font-outfit text-eai-body font-semibold text-eai-ink">
                            {userName}
                          </div>
                        </>
                      ) : (
                        <ErodeLogo className="h-4 w-auto" aria-label="Erode" />
                      )}
                    </div>
                    {/* Action buttons (top-right of bubble) — hidden for pending/error bubbles */}
                    {!isPendingMsg && !isErrorMsg && (
                      <div className="flex shrink-0 items-center gap-2">
                        {/* Embed live result — only for scheduled-agent runs (needs a runnerId) */}
                        {!readOnly && scheduleRunnerId && (
                          <button
                            type="button"
                            onClick={openEmbedModal}
                            aria-label="Share or embed live result"
                            title="Share or embed live result"
                            className="text-eai-ink-faint transition-colors hover:text-eai-ink"
                          >
                            <FiCode className="h-4 w-4" />
                          </button>
                        )}
                        {/* Thumbs up / down on an AI response (owner-only contexts; hidden when read-only) */}
                        {!readOnly && conversationId && !msg.isUser && (
                          <>
                            <button
                              type="button"
                              data-testid="chat-thumbs-up"
                              onClick={() => handleRate(msg, "UP")}
                              aria-label="Good response"
                              aria-pressed={msg.rating === "UP"}
                              title="Good response"
                              className={`transition-colors ${
                                msg.rating === "UP"
                                  ? "text-eai-cta-primary"
                                  : "text-eai-ink-faint hover:text-eai-ink"
                              }`}
                            >
                              {msg.rating === "UP" ? (
                                <FaThumbsUp className="h-3.5 w-3.5" />
                              ) : (
                                <FiThumbsUp className="h-3.5 w-3.5" />
                              )}
                            </button>
                            <button
                              type="button"
                              data-testid="chat-thumbs-down"
                              onClick={() => handleRate(msg, "DOWN")}
                              aria-label="Bad response"
                              aria-pressed={msg.rating === "DOWN"}
                              title="Bad response"
                              className={`transition-colors ${
                                msg.rating === "DOWN"
                                  ? "text-eai-danger-text"
                                  : "text-eai-ink-faint hover:text-eai-ink"
                              }`}
                            >
                              {msg.rating === "DOWN" ? (
                                <FaThumbsDown className="h-3.5 w-3.5" />
                              ) : (
                                <FiThumbsDown className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </>
                        )}
                        {/* Share this single message (hidden in read-only contexts) */}
                        {!readOnly && conversationId && (
                          <button
                            type="button"
                            onClick={() => handleShareMessage(msg.id)}
                            aria-label="Share this message"
                            title="Share this message"
                            className="text-eai-ink-faint transition-colors hover:text-eai-ink"
                          >
                            <FiShare2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {/* Expand-to-full-screen button */}
                        <button
                          type="button"
                          onClick={() => setExpanded({ message: msg, triggerText })}
                          aria-label="Expand message to full screen"
                          title="Expand to full screen"
                          className="text-eai-ink-faint transition-colors hover:text-eai-ink"
                        >
                          <FiMaximize2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    {msg.isUser ? (
                      <MessageWithMentions text={stripAssetContext(msg.text)} readOnly={readOnly} />
                    ) : isPendingMsg ? (
                      <>
                        <div data-testid="chat-thinking" className="animate-pulse">Thinking...</div>
                        {showDelayMessage && (
                          <div className="font-inter mt-2 text-xs text-eai-ink-subtle">
                            Complex questions can take longer to answer, we'll let you know when we're done processing your results.
                          </div>
                        )}
                      </>
                    ) : isErrorMsg ? (
                      <div>
                        <div className="font-semibold text-eai-danger-text">Error</div>
                        <div className="mt-1 text-eai-danger-text">
                          We were unable to process this request. Try breaking your task up into smaller steps, or try again later.
                        </div>
                        {!readOnly && onRetry && triggerText && (
                          <button
                            type="button"
                            onClick={() => onRetry(triggerText)}
                            className="eai-btn-danger-xs mt-2"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <MessageWithChart
                          message={responseText}
                          triggerText={lastUserMessage?.text}
                          conversationId={conversationId}
                          messageId={msg.id}
                          readOnly={readOnly}
                        />
                        {msg.metadata?.isScheduled && (
                          <div className="mt-1 font-inter flex items-center gap-1 text-xs text-eai-cta-primary">
                            🗓 Schedule created
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {/* Thumbs-down follow-up: optional comment, sent to the admin error-log queue */}
                  {feedbackDraft?.messageId === msg.id && (
                    <div
                      data-testid="chat-feedback-box"
                      className="font-inter mt-3 flex flex-col gap-2 rounded-eai-card border border-eai-border bg-eai-surface-card p-2 sm:p-3"
                    >
                      <label htmlFor={`feedback-${msg.id}`} className="text-eai-caption text-eai-ink-muted">
                        Sorry about that. What went wrong? (optional)
                      </label>
                      <textarea
                        id={`feedback-${msg.id}`}
                        value={feedbackDraft.comment}
                        onChange={(e) =>
                          setFeedbackDraft((d) =>
                            d ? { ...d, comment: e.target.value.slice(0, CHAT_FEEDBACK_COMMENT_MAX) } : d
                          )
                        }
                        rows={2}
                        maxLength={CHAT_FEEDBACK_COMMENT_MAX}
                        placeholder="e.g. the numbers don't match the forecast, it ignored my question…"
                        className="eai-input resize-none text-eai-body-sm"
                        disabled={feedbackDraft.submitting}
                        autoFocus
                      />
                      {feedbackDraft.error && (
                        <div className="text-eai-caption text-eai-danger-text">{feedbackDraft.error}</div>
                      )}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleSubmitFeedback(msg, triggerText, feedbackDraft.comment)}
                          disabled={feedbackDraft.submitting}
                          className="eai-btn-cta-xs"
                        >
                          {feedbackDraft.submitting ? "Sending…" : "Send"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSubmitFeedback(msg, triggerText, "")}
                          disabled={feedbackDraft.submitting}
                          className="eai-btn-outline-xs"
                        >
                          Skip
                        </button>
                      </div>
                    </div>
                  )}
                  {feedbackSentFor === msg.id && (
                    <div className="font-inter mt-2 text-eai-caption text-eai-ink-muted" role="status">
                      Thanks, your feedback has been sent.
                    </div>
                  )}
                  {/* Render file attachments for user messages. In readOnly mode only KML attachments are shown (with map preview). In normal mode all attachments are shown; KML uses preview chip. */}
                  {msg.isUser && msg.attachments && msg.attachments.length > 0 && (() => {
                    const visibleAttachments = readOnly
                      ? msg.attachments.filter((f) => f.filename?.toLowerCase().endsWith('.kml'))
                      : msg.attachments;
                    if (visibleAttachments.length === 0) return null;
                    return (
                      <div className="mt-2 flex flex-col gap-1.5">
                        {visibleAttachments.map((file) =>
                          file.filename?.toLowerCase().endsWith('.kml') ? (
                            <KmlPreviewChip key={file.uri} file={file} readOnly={readOnly} deferLoad={msg.id.endsWith("-pending")} />
                          ) : (
                            <FileChip key={file.uri} file={file} />
                          )
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              {/* Timestamp display outside bubble */}
              {msg.createdAt && (
                <div className={`font-inter mt-1 text-eai-caption text-eai-ink-faint ${
                  msg.isUser ? "text-right pr-1" : "text-left pl-1"
                }`}>
                  {formatMessageTime(msg.createdAt)}
                </div>
              )}
            </div>
          );
        });
      })()}

      {isLoading && !hasPendingMessage && (
        <div className="flex items-start gap-1.5 sm:gap-3">
          <div className="font-inter inline-block min-w-[140px] max-w-[95%] sm:max-w-[75%] whitespace-pre-wrap break-words rounded-eai-card p-2 sm:p-3 text-left text-eai-body-sm shadow-eai-subtle border border-eai-border bg-eai-checklist-lt text-eai-ink-soft">
            <div className="flex items-center gap-2 mb-3">
              <ErodeLogo className="h-4 w-auto" aria-label="Erode" />
            </div>
            <div data-testid="chat-thinking" className="animate-pulse">
              Thinking...
            </div>
            {showDelayMessage && (
              <div className="font-inter mt-2 text-xs text-eai-ink-subtle">
                Complex questions can take longer to answer, we'll let you know when we're done processing your results.
              </div>
            )}
          </div>
        </div>
      )}
      {isFetchingConversation && (
        // Sized like the bubbles it replaces, alternating sides, so nothing jumps when they land
        <SkeletonGroup count={2} label="Loading conversation" className="space-y-4">
          <>
            <SkeletonText lines={2} className="max-w-[75%] sm:max-w-[60%]" />
            <SkeletonText lines={3} className="ml-auto max-w-[95%] sm:max-w-[75%]" />
          </>
        </SkeletonGroup>
      )}
      <div ref={messagesEndRef} />

      {/* Full-screen expanded view of a chat bubble */}
      <ChatBubbleModal
        message={expanded?.message ?? null}
        userName={userName}
        userProfileUrl={user?.permissionDetails?.profilePictureUrl}
        triggerText={expanded?.triggerText}
        conversationId={conversationId}
        onClose={handleCloseExpanded}
      />
    </>
  );
};

export default AiChatMessages;
